import React from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { renderWithPrivateName } from './privacyName';
import {
  type PatientStatusAction,
  type PatientStatusDimension,
} from './railPatient';

const COPY_KEYS = {
  deceased: {
    set: {
      title: 'patients.status.deceasedSetTitle',
      description: 'patients.status.deceasedSetDescription',
      confirmLabel: 'patients.status.deceasedSetConfirm',
    },
    clear: {
      title: 'patients.status.deceasedClearTitle',
      description: 'patients.status.deceasedClearDescription',
      confirmLabel: 'patients.status.deceasedClearConfirm',
    },
  },
  'left-practice': {
    set: {
      title: 'patients.status.leftSetTitle',
      description: 'patients.status.leftSetDescription',
      confirmLabel: 'patients.status.leftSetConfirm',
    },
    clear: {
      title: 'patients.status.leftClearTitle',
      description: 'patients.status.leftClearDescription',
      confirmLabel: 'patients.status.leftClearConfirm',
    },
  },
} as const satisfies Record<
  PatientStatusDimension,
  Record<
    'set' | 'clear',
    { title: string; description: string; confirmLabel: string }
  >
>;

export const PatientStatusConfirmDialog: React.FC<{
  action: PatientStatusAction | null;
  patientName: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ action, patientName, isPending, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const copy = action
    ? COPY_KEYS[action.dimension][action.set ? 'set' : 'clear']
    : null;
  return (
    <AlertDialog
      open={action !== null}
      onOpenChange={(open) => {
        if (!open && !isPending) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {copy && renderWithPrivateName(t(copy.title), patientName)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {copy && t(copy.description)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            data-testid="confirm-patient-status"
            onClick={(event) => {
              // Keep the dialog open while the write is in flight; the hook
              // clears the action (and closes it) when the write settles.
              event.preventDefault();
              onConfirm();
            }}
          >
            {isPending
              ? t('patients.common.saving')
              : copy && t(copy.confirmLabel)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
